import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Avatar,
    Chip,
    Stack,
    Divider,
    CircularProgress
} from '@mui/material';
import {
    Person as PersonIcon,
    Phone as PhoneIcon,
    Schedule as ScheduleIcon,
    AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import moment from 'moment';
import api from '../services/api';
import ToastError from '../toast/error/toastError';

const RescheduleTicketPreview = ({ open, onClose, rescheduleTicketId }) => {
    const [rescheduleTicket, setRescheduleTicket] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchRescheduleTicket = async () => {
            if (!rescheduleTicketId || !open) return;

            try {
                setLoading(true);
                const { data } = await api.get(`/reschedule-tickets/${rescheduleTicketId}`);
                setRescheduleTicket(data);
            } catch (error) {
                console.error('Erro ao carregar ticket reagendado:', error);
                ToastError('Erro ao carregar detalhes do ticket reagendado');
                onClose();
            } finally {
                setLoading(false);
            }
        };

        fetchRescheduleTicket();
    }, [rescheduleTicketId, open]);

    const handleClose = () => {
        setRescheduleTicket(null);
        onClose();
    };

    const getStatusLabel = (status) => {
        const statusMap = {
            pending: { label: 'Pendente', color: 'warning' },
            executed: { label: 'Executado', color: 'success' },
            cancelled: { label: 'Cancelado', color: 'error' }
        };
        return statusMap[status] || { label: status, color: 'default' };
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon color="primary" />
                    <Typography variant="h6">Ticket Reagendado</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                ) : rescheduleTicket ? (
                    <Stack spacing={2}>
                        {/* Status */}
                        <Box>
                            <Chip
                                label={getStatusLabel(rescheduleTicket.status).label}
                                color={getStatusLabel(rescheduleTicket.status).color}
                                size="small"
                            />
                        </Box>

                        <Divider />

                        {/* Contato */}
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Contato
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                    src={rescheduleTicket.contact?.profilePicUrl}
                                    sx={{ width: 40, height: 40 }}
                                >
                                    {rescheduleTicket.contact?.name?.charAt(0)?.toUpperCase()}
                                </Avatar>
                                <Box>
                                    <Typography variant="body1" fontWeight={500}>
                                        {rescheduleTicket.contact?.name || 'Sem nome'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {rescheduleTicket.contact?.number}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Divider />

                        {/* Data e Hora */}
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                <ScheduleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                Data e Hora do Reagendamento
                            </Typography>
                            <Typography variant="body1">
                                {moment(rescheduleTicket.rescheduleDateAt).format('DD/MM/YYYY')} às {rescheduleTicket.rescheduleTimeAt}
                            </Typography>
                        </Box>

                        <Divider />

                        {/* Usuário Responsável */}
                        {rescheduleTicket.user && (
                            <>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        <AccountCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        Responsável
                                    </Typography>
                                    <Typography variant="body1">
                                        {rescheduleTicket.user.name}
                                    </Typography>
                                </Box>
                                <Divider />
                            </>
                        )}

                        {/* Fila */}
                        {rescheduleTicket.queue && (
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Fila
                                </Typography>
                                <Chip
                                    label={rescheduleTicket.queue.name}
                                    size="small"
                                    sx={{
                                        backgroundColor: rescheduleTicket.queue.color,
                                        color: '#fff'
                                    }}
                                />
                            </Box>
                        )}

                        {/* Ticket Original */}
                        {rescheduleTicket.ticket && (
                            <>
                                <Divider />
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Ticket Original
                                    </Typography>
                                    <Typography variant="body2">
                                        ID: #{rescheduleTicket.ticketId}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RescheduleTicketPreview;
